import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarOff, ChevronLeft, ChevronRight, ListChecks, Lock, PartyPopper, Plus, Repeat, Trash2 } from 'lucide-react';
import { apiPersons, apiSchedule, apiSettings } from '../api/client.js';
import MobileAllocationModal from './MobileAllocationModal.jsx';
import MobileUnavailabilityModal, { isOwnUnavailability } from './MobileUnavailabilityModal.jsx';
import MobileMonthGrid from './MobileMonthGrid.jsx';
import { useMonthCursor } from './mobileView.js';
import { formatHHMM } from '../components/DurationInput.jsx';
import {
  todayUtc,
  mondayOf,
  addDays,
  isoDate,
  weekDays,
  fullDayLabel,
  weekRangeLabel,
  safeColor,
  safeColorAlpha,
  monthGridDays,
  parseIso,
  workingDayMask,
} from './dateUtils.js';
import { halfDayLabel } from '../components/HalfDayPicker.jsx';

/** Nombre max de barres d'affaire dans une cellule de la grille mensuelle. */
const MAX_CAL_BARS = 3;

/**
 * Schedule mobile — vue SEMAINE (liste verticale par jour) ou MOIS
 * (`viewMode === 'calendar'` : grille + détail du jour sélectionné, rendu par
 * le même `ScheduleDayCard` que la liste).
 * Se rafraîchit en temps réel via SSE (event `schedule.change`).
 *
 * PWA web : lecture seule. App native (`selfService`, « Mon planning ») :
 * création / modification / suppression des allocations de SA Person si le
 * réglage `selfScheduleEnabled` est actif et que le rôle n'est pas VIEWER
 * (règles Sprint 16.B ; le serveur reste l'autorité). Un sous-traitant y
 * déclare et supprime aussi ses indisponibilités (#106), indépendamment de
 * `selfScheduleEnabled`, comme sur le web.
 */
export default function MobileSchedule({ user, selfService = false, viewMode = 'list' }) {
  const isCalendar = viewMode === 'calendar';
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayUtc()));
  const { month, setMonth, selectedIso, setSelectedIso } = useMonthCursor();
  const [myPersonId, setMyPersonId] = useState(null);
  const [isContractor, setIsContractor] = useState(false);
  const [personResolved, setPersonResolved] = useState(false);
  const [unavailability, setUnavailability] = useState(null); // { mode, date?, timeOff? }
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Self-service (natif) : réglage lu une fois ; `null` = pas encore connu.
  const [selfScheduleEnabled, setSelfScheduleEnabled] = useState(null);
  const [modal, setModal] = useState(null); // { mode, allocation?, date?, startDelete? }
  const [toast, setToast] = useState(null); // { kind, message }

  useEffect(() => {
    if (!selfService) return undefined;
    let cancelled = false;
    apiSettings
      .get()
      .then((s) => {
        if (!cancelled) setSelfScheduleEnabled(Boolean(s?.selfScheduleEnabled));
      })
      .catch(() => {
        if (!cancelled) setSelfScheduleEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selfService]);

  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Résout la Person liée une seule fois.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await apiPersons.me();
        if (!cancelled) {
          setMyPersonId(me?.id ?? null);
          setIsContractor(me?.employeeType === 'CONTRACTOR');
        }
      } catch {
        if (!cancelled) setMyPersonId(null);
      } finally {
        if (!cancelled) setPersonResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fenêtre chargée : la semaine (liste) ou la grille du mois (calendrier,
  // 28 à 42 jours) — toujours un seul `GET /api/schedule`.
  const { from, to } = useMemo(() => {
    if (isCalendar) {
      const grid = monthGridDays(month);
      return { from: isoDate(grid[0]), to: isoDate(grid[grid.length - 1]) };
    }
    return { from: isoDate(weekStart), to: isoDate(addDays(weekStart, 6)) };
  }, [isCalendar, month, weekStart]);

  // Une seule requête en vol : la précédente (fenêtre devenue obsolète) est
  // annulée, et sa réponse éventuelle ignorée.
  const abortRef = useRef(null);
  useEffect(() => () => abortRef.current?.abort(), []);
  const fetchWeek = useCallback(
    async (silent = false) => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      if (!silent) setLoading(true);
      try {
        const sched = await apiSchedule.get({
          from,
          to,
          personIds: myPersonId ? [myPersonId] : undefined,
          withHolidays: true,
          signal: ctrl.signal,
        });
        if (abortRef.current !== ctrl) return;
        setData(sched);
        setError(null);
      } catch (e) {
        if (abortRef.current !== ctrl || e?.name === 'AbortError') return;
        // 403 `mobile_scope` (app native) : message lisible plutôt que générique.
        setError(e?.code === 'mobile_scope' ? e.message : 'load');
      } finally {
        if (abortRef.current === ctrl) setLoading(false);
      }
    },
    [from, to, myPersonId],
  );

  useEffect(() => {
    if (!personResolved) return;
    fetchWeek(false);
  }, [personResolved, fetchWeek]);

  // SSE : re-fetch silencieux sur mutation. Ref pour toujours pointer la
  // dernière version de fetchWeek sans relancer le stream.
  const fetchRef = useRef(fetchWeek);
  fetchRef.current = fetchWeek;
  useEffect(() => {
    const conn = apiSchedule.stream({
      onEvent: (evt) => {
        if (evt?.event === 'schedule.change') fetchRef.current?.(true);
      },
    });
    return () => conn?.close?.();
  }, []);

  const canEdit =
    selfService &&
    selfScheduleEnabled === true &&
    user?.role !== 'VIEWER' &&
    Boolean(myPersonId);
  // #106 — indisponibilité : sous-traitant, hors VIEWER, sans le réglage de
  // planification en autonomie (le serveur en est l'autorité).
  const canDeclareUnavailability =
    selfService && isContractor && user?.role !== 'VIEWER' && Boolean(myPersonId);
  let readOnlyReason = null;
  if (selfService && personResolved && !myPersonId) {
    readOnlyReason = 'Aucune fiche collaborateur liée à votre compte.';
  } else if (selfService && selfScheduleEnabled === false) {
    readOnlyReason = 'Lecture seule : la planification en autonomie n’est pas activée.';
  } else if (selfService && user?.role === 'VIEWER') {
    readOnlyReason = 'Lecture seule : votre profil ne permet pas de planifier.';
  }

  const handleSaved = (message) => {
    setModal(null);
    setUnavailability(null);
    showToast('ok', message);
    fetchWeek(true);
  };

  const days = weekDays(weekStart);
  const todayIso = isoDate(todayUtc());
  const allocations = data?.allocations ?? [];
  const timeOffs = data?.timeOffs ?? [];
  const holidays = (myPersonId && data?.holidaysByPerson?.[myPersonId]) || [];
  const holidayByIso = new Map(holidays.map((h) => [String(h.date).slice(0, 10), h]));

  const me = (data?.persons ?? []).find((p) => p.id === myPersonId);
  const workingMask = workingDayMask(me?.workDaysHours);

  function blocksForDay(iso) {
    const offs = timeOffs.filter(
      (t) => String(t.startDate).slice(0, 10) <= iso && iso <= String(t.endDate).slice(0, 10),
    );
    // Même règle que le Schedule (`splitAllocationIntoSegments`) : une
    // allocation du 1er au 30 couvre les week-ends en base, mais ne s'affiche
    // pas un jour non travaillé, férié ou en congé approuvé journée entière.
    const dayOff =
      !workingMask[parseIso(iso).getUTCDay()] ||
      holidayByIso.has(iso) ||
      offs.some((t) => t.fullDay !== false && t.status === 'APPROVED');
    const allocs = dayOff
      ? []
      : allocations.filter(
          (a) => String(a.startDate).slice(0, 10) <= iso && iso <= String(a.endDate).slice(0, 10),
        );
    return { allocs, offs };
  }

  const onCreate = (iso) => setModal({ mode: 'create', date: iso });
  const onEdit = (a) => setModal({ mode: 'edit', allocation: a });
  const onDelete = (a) => setModal({ mode: 'edit', allocation: a, startDelete: true });
  const onDeclareUnavailability = (iso) => setUnavailability({ mode: 'create', date: iso });
  const onDeleteUnavailability = (t) => setUnavailability({ mode: 'delete', timeOff: t });

  const renderDay = (d) => {
    const iso = isoDate(d);
    const { allocs, offs } = blocksForDay(iso);
    return (
      <ScheduleDayCard
        key={iso}
        date={d}
        iso={iso}
        allocs={allocs}
        offs={offs}
        holiday={holidayByIso.get(iso)}
        isToday={iso === todayIso}
        canEdit={canEdit}
        canDeclareUnavailability={canDeclareUnavailability}
        onCreate={onCreate}
        onEdit={onEdit}
        onDelete={onDelete}
        onDeclareUnavailability={onDeclareUnavailability}
        onDeleteUnavailability={onDeleteUnavailability}
      />
    );
  };

  // Cellule de la grille : ≤ 3 barres (affaires puis congés), `+n`, total.
  const renderCell = (_d, iso) => {
    const { allocs, offs } = blocksForDay(iso);
    const bars = [
      ...allocs.map((a) => ({ key: a.instanceId, color: safeColor(a.affaire?.color), off: false })),
      ...offs.map((t) => ({
        key: t.instanceId,
        color: safeColor(t.timeOffType?.colorHex, 'var(--text-muted)'),
        off: true,
      })),
    ];
    const hours = allocs.reduce((acc, a) => acc + (Number(a.hoursPerDay) || 0), 0);
    return (
      <>
        <span className="m-cal__bars">
          {bars.slice(0, MAX_CAL_BARS).map((b) => (
            <span
              key={b.key}
              className="m-cal__bar"
              data-off={b.off}
              style={{ background: b.color }}
            />
          ))}
        </span>
        <span className="m-cal__foot">
          {bars.length > MAX_CAL_BARS ? (
            <span className="m-cal__more">+{bars.length - MAX_CAL_BARS}</span>
          ) : null}
          {hours > 0 ? <span className="m-cal__hours">{formatHHMM(hours)}</span> : null}
        </span>
      </>
    );
  };

  const cellLabel = (_d, iso) => {
    const { allocs, offs } = blocksForDay(iso);
    const parts = [];
    if (allocs.length) parts.push(`${allocs.length} allocation(s)`);
    if (offs.length) parts.push(`${offs.length} congé(s)`);
    return parts.join(', ');
  };

  const stateBlock = loading ? (
    <div className="m-state">Chargement…</div>
  ) : error ? (
    <div className="m-state">
      {error === 'load' ? 'Impossible de charger le planning.' : error}
    </div>
  ) : null;

  const notice = readOnlyReason ? (
    <div className="m-notice">
      <Lock size={14} style={{ flexShrink: 0 }} />
      <span>{readOnlyReason}</span>
    </div>
  ) : null;

  const overlays = (
    <>
      {selfService ? (
        <MobileAllocationModal
          open={Boolean(modal)}
          mode={modal?.mode}
          allocation={modal?.allocation}
          defaultDate={modal?.date}
          personId={myPersonId}
          startDelete={Boolean(modal?.startDelete)}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      ) : null}

      {canDeclareUnavailability ? (
        <MobileUnavailabilityModal
          open={Boolean(unavailability)}
          mode={unavailability?.mode}
          timeOff={unavailability?.timeOff}
          defaultDate={unavailability?.date}
          personId={myPersonId}
          onClose={() => setUnavailability(null)}
          onSaved={handleSaved}
        />
      ) : null}

      {toast ? (
        <div className="m-toast" data-kind={toast.kind === 'error' ? 'error' : 'ok'}>
          {toast.message}
        </div>
      ) : null}
    </>
  );

  if (isCalendar) {
    return (
      <div>
        <MobileMonthGrid
          month={month}
          onMonthChange={setMonth}
          selectedIso={selectedIso}
          onSelect={setSelectedIso}
          renderCell={renderCell}
          cellLabel={cellLabel}
          holidayByIso={holidayByIso}
          loading={loading}
        />
        <div className="m-cal__detail">
          {notice}
          {stateBlock ?? renderDay(parseIso(selectedIso))}
        </div>
        {overlays}
      </div>
    );
  }

  return (
    <div>
      <div className="m-weeknav">
        <button
          type="button"
          className="m-navbtn"
          aria-label="Semaine précédente"
          onClick={() => setWeekStart((w) => addDays(w, -7))}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="m-weeknav__title">{weekRangeLabel(weekStart)}</span>
        <button
          type="button"
          className="m-navbtn"
          aria-label="Semaine suivante"
          onClick={() => setWeekStart((w) => addDays(w, 7))}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <button
          type="button"
          className="m-todaybtn"
          onClick={() => setWeekStart(mondayOf(todayUtc()))}
        >
          Cette semaine
        </button>
      </div>

      {notice}

      {stateBlock ?? days.map(renderDay)}

      {overlays}
    </div>
  );
}

/**
 * Rendu d'UN jour du planning (en-tête + allocations + congés), partagé par la
 * liste semaine et le détail du jour sélectionné dans la grille mensuelle.
 * `canEdit` : bouton « + » (création) et allocations cliquables (édition /
 * suppression via `MobileAllocationModal`).
 */
function ScheduleDayCard({
  date,
  iso,
  allocs,
  offs,
  holiday,
  isToday,
  canEdit,
  canDeclareUnavailability,
  onCreate,
  onEdit,
  onDelete,
  onDeclareUnavailability,
  onDeleteUnavailability,
}) {
  return (
    <div className="m-day">
      <div className="m-day__head" data-today={isToday} data-holiday={Boolean(holiday)}>
        <span>{fullDayLabel(date)}</span>
        {holiday ? (
          <span
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'var(--warning)',
              fontSize: 12,
            }}
          >
            <PartyPopper size={13} /> {holiday.name}
          </span>
        ) : null}
        {canDeclareUnavailability ? (
          <button
            type="button"
            className="m-daybtn"
            style={holiday ? undefined : { marginLeft: 'auto' }}
            onClick={() => onDeclareUnavailability(iso)}
            title="Déclarer une indisponibilité"
            aria-label={`Déclarer une indisponibilité le ${fullDayLabel(date)}`}
          >
            <CalendarOff size={16} />
          </button>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            className="m-daybtn"
            style={holiday || canDeclareUnavailability ? undefined : { marginLeft: 'auto' }}
            onClick={() => onCreate(iso)}
            title="Ajouter une allocation"
            aria-label={`Ajouter une allocation le ${fullDayLabel(date)}`}
          >
            <Plus size={16} />
          </button>
        ) : null}
      </div>
      {allocs.length === 0 && offs.length === 0 ? (
        <div className="m-day__empty">—</div>
      ) : (
        <div className="m-day__body">
          {allocs.map((a) => (
            <div
              className="m-block"
              key={a.instanceId}
              style={{ background: safeColorAlpha(a.affaire?.color) }}
              {...(canEdit
                ? {
                    role: 'button',
                    tabIndex: 0,
                    'data-clickable': true,
                    title: 'Modifier l’allocation',
                    onClick: () => onEdit(a),
                    onKeyDown: (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onEdit(a);
                      }
                    },
                  }
                : {})}
            >
              <span className="m-block__dot" style={{ background: safeColor(a.affaire?.color) }} />
              <div style={{ minWidth: 0 }}>
                <div className="m-block__name">
                  {a.affaire?.name || a.name || '—'}
                  {a.isRecurring ? (
                    <Repeat size={12} style={{ marginLeft: 6, verticalAlign: 'middle' }} />
                  ) : null}
                </div>
                {a.phase?.name ? <div className="m-block__sub">{a.phase.name}</div> : null}
                {a.task?.name ? (
                  <div className="m-block__sub m-logrow__meta">
                    <ListChecks size={12} aria-hidden="true" /> {a.task.name}
                  </div>
                ) : null}
              </div>
              <span className="m-block__hours">{formatHHMM(a.hoursPerDay)}</span>
              {canEdit ? (
                <button
                  type="button"
                  className="m-iconbtn m-iconbtn--sm m-iconbtn--danger"
                  // Le bloc entier ouvre l'édition : la corbeille ne doit pas
                  // déclencher ce clic-là en plus.
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(a);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  title="Supprimer l’allocation"
                  aria-label={`Supprimer l’allocation ${a.affaire?.name || ''}`}
                >
                  <Trash2 size={15} />
                </button>
              ) : null}
            </div>
          ))}
          {offs.map((t) => (
            <div
              className="m-block"
              key={t.instanceId}
              style={{ background: safeColorAlpha(t.timeOffType?.colorHex) }}
            >
              <span
                className="m-block__dot"
                style={{ background: safeColor(t.timeOffType?.colorHex, 'var(--text-muted)') }}
              />
              <div style={{ minWidth: 0 }}>
                <div className="m-block__name">{t.timeOffType?.name || 'Congé'}</div>
                {t.notes ? <div className="m-block__sub">{t.notes}</div> : null}
              </div>
              <span className="m-block__hours">
                {t.fullDay ? (halfDayLabel(t, { short: true }) ?? 'Jour') : formatHHMM(t.hoursPerDay)}
              </span>
              {canDeclareUnavailability && isOwnUnavailability(t) ? (
                <button
                  type="button"
                  className="m-iconbtn m-iconbtn--sm m-iconbtn--danger"
                  onClick={() => onDeleteUnavailability(t)}
                  title="Supprimer l’indisponibilité"
                  aria-label="Supprimer l’indisponibilité"
                >
                  <Trash2 size={15} />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
